import { useState, useEffect } from 'react';
import { Minus } from 'lucide-react';
import { financeV2Service, CreateTransactionDto, PayTransactionDto } from '../../../services/financeV2Service';
import { clientService } from '../../../services/client-service';
import { caseService } from '../../../services/case-service';
import type { Client } from '../../../types/client';
import { CaseStatus, type Case } from '../../../types/case';
import type { Account, Category } from '../../../types/financeV2.types';
import { NativeSelect } from "@/components/ui/native-select"
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'

import { getErrorMessage } from '../../../utils/error.utils'
interface QuickExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function QuickExpenseModal({ isOpen, onClose, onSuccess }: QuickExpenseModalProps) {
  // Form state
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [clientId, setClientId] = useState('');
  const [caseId, setCaseId] = useState('');
  const [notes, setNotes] = useState('');

  // Pay immediately option
  const [payImmediately, setPayImmediately] = useState(true);

  // Data loading
  const [clients, setClients] = useState<Client[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  // Load initial data
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  // Load cases when client is selected
  useEffect(() => {
    if (clientId) {
      loadClientCases(clientId);
    } else {
      setCases([]);
      setCaseId('');
    }
  }, [clientId]);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      const [clientsRes, accountsRes, categoriesRes] = await Promise.all([
        clientService.getAllClients({ status: 'ACTIVE' }, { page: 1, limit: 1000, sort: 'fullName:asc' }),
        financeV2Service.accounts.list({ isActive: true, limit: 100 }),
        financeV2Service.categories.list({ type: 'EXPENSE', isActive: true, limit: 100 })
      ]);

      setClients(clientsRes.clients || []);
      setAccounts(accountsRes.data.accounts || []);
      setCategories(categoriesRes.data.categories || []);

      // Set default account (first active account)
      if (accountsRes.data.accounts && accountsRes.data.accounts.length > 0) {
        setAccountId(accountsRes.data.accounts[0].id);
      }

      // Set default category (look for common expense categories)
      const defaultCategory = categoriesRes.data.categories?.find(c =>
        c.name.toLowerCase().includes('escritório') ||
        c.name.toLowerCase().includes('operacional')
      );
      if (defaultCategory) {
        setCategoryId(defaultCategory.id);
      } else if (categoriesRes.data.categories && categoriesRes.data.categories.length > 0) {
        setCategoryId(categoriesRes.data.categories[0].id);
      }

    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar dados'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadClientCases = async (clientId: string) => {
    try {
      const casesRes = await caseService.getAllCases(
        { clientId, status: [CaseStatus.ACTIVE, CaseStatus.SUSPENDED] },
        { page: 1, limit: 100, sort: 'createdAt:desc' }
      );
      setCases(casesRes.cases || []);
    } catch (err) {
      console.error('Error loading client cases:', err);
    }
  };

  // Helper function to get client display name
  const getClientDisplayName = (client: Client): string => {
    if (client.clientType === 'PERSON') {
      return client.personalData?.fullName ||
             `${client.personalData?.firstName || ''} ${client.personalData?.lastName || ''}`.trim() ||
             'Cliente sem nome';
    } else {
      return client.companyData?.companyName ||
             client.companyData?.tradingName ||
             'Empresa sem nome';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description || !amount || !expenseDate || !accountId || !categoryId) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Valor inválido');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build metadata
      const metadata: Record<string, unknown> = {
        simplified: true
      };
      if (clientId) {
        metadata.clientId = clientId;
      }
      if (caseId) {
        metadata.caseId = caseId;
      }

      // Create expense transaction
      const transactionData: CreateTransactionDto = {
        description,
        amount: numAmount,
        type: 'EXPENSE',
        accountId,
        categoryId,
        dueDate: expenseDate,
        notes: notes || undefined,
        metadata
      };

      const createResponse = await financeV2Service.transactions.create(transactionData);

      // If pay immediately, mark as paid
      if (payImmediately) {
        const transactionId = createResponse.data.transaction.id;
        const payData: PayTransactionDto = {
          paidDate: expenseDate,
          paidAmount: numAmount,
          notes: 'Despesa paga no ato'
        };

        await financeV2Service.transactions.pay(transactionId, payData);
      }

      // Success!
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao registrar despesa'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setDescription('');
    setAmount('');
    setExpenseDate(new Date().toISOString().split('T')[0]);
    setCategoryId('');
    setClientId('');
    setCaseId('');
    setNotes('');
    setPayImmediately(true);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) handleClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Registrar Despesa Rápida</DialogTitle>
          <DialogDescription>Gasto do escritório, paga no ato ou deixada em aberto.</DialogDescription>
        </DialogHeader>

        {loadingData ? (
          <div className="flex flex-col gap-4" aria-busy="true">
            <span className="sr-only">Carregando dados...</span>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : (
          <form id="quick-expense-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
            {error && (
              <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <FormField label="Descrição *" htmlFor="expense-description">
              <Input
                id="expense-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Pagamento de aluguel do escritório"
                required
              />
            </FormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Valor *" htmlFor="expense-amount">
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                  <Input
                    id="expense-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0,00"
                    className="pl-9"
                    required
                  />
                </div>
              </FormField>

              <FormField label="Data da Despesa *" htmlFor="expense-date">
                <Input
                  id="expense-date"
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  required
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Categoria *" htmlFor="expense-category">
                <NativeSelect
                  id="expense-category"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>

              <FormField label="Conta *" htmlFor="expense-account">
                <NativeSelect
                  id="expense-account"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  required
                >
                  <option value="">Selecione uma conta</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.type})
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
            </div>

            <fieldset className="flex min-w-0 flex-col gap-3 rounded-lg border bg-muted/50 p-3">
              <legend className="px-1 text-sm font-medium">Vínculo (opcional)</legend>

              <FormField label="Cliente" htmlFor="expense-client">
                <NativeSelect
                  id="expense-client"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  <option value="">Sem vínculo com cliente</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {getClientDisplayName(client)}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>

              {/* Processo (só aparece se cliente for selecionado) */}
              {clientId && (
                <FormField label="Processo" htmlFor="expense-case">
                  <NativeSelect
                    id="expense-case"
                    value={caseId}
                    onChange={(e) => setCaseId(e.target.value)}
                    disabled={cases.length === 0}
                  >
                    <option value="">Sem vínculo com processo</option>
                    {cases.map((caseItem) => (
                      <option key={caseItem.id} value={caseItem.id}>
                        {caseItem.caseNumber} - {caseItem.title}
                      </option>
                    ))}
                  </NativeSelect>
                </FormField>
              )}
            </fieldset>

            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="payImmediately"
                  checked={payImmediately}
                  onChange={(e) => setPayImmediately(e.target.checked)}
                  className="size-4 rounded-sm border-input accent-primary"
                />
                <Label htmlFor="payImmediately" className="font-normal">Marcar como paga imediatamente</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {payImmediately
                  ? 'A despesa será registrada e marcada como paga na mesma data'
                  : 'A despesa ficará como pendente de pagamento'}
              </p>
            </div>

            <FormField label="Observações" htmlFor="expense-notes">
              <Textarea
                id="expense-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Informações adicionais sobre esta despesa..."
                className="resize-none"
              />
            </FormField>

            {/* Future feature placeholder */}
            <div className="flex flex-col gap-2 rounded-lg border bg-muted/50 p-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="expense-attachment"
                  disabled
                  className="size-4 rounded-sm border-input accent-primary"
                />
                <Label htmlFor="expense-attachment" className="font-normal text-muted-foreground">
                  Anexar comprovante (em breve)
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Funcionalidade de anexo de comprovantes será implementada em breve
              </p>
            </div>
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="quick-expense-form" loading={loading} disabled={loadingData}>
            {loading ? 'Registrando...' : <><Minus className="size-4" /> Registrar Despesa</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
